/**
 * Team Spain v0 — Servicio de Planificación Deportiva (M3)
 *
 * Gestión de:
 * 1. Catálogo ofensivo (conceptos y jugadas)
 * 2. Catálogo defensivo (llamadas y familias de cobertura)
 * 3. Quintetos reutilizables (plantillas y slots con 5 posiciones)
 * 4. Guiones de sesión (práctica guiada / Team competitivo y repeticiones planificadas)
 * 5. Detección de conflictos de atleta dual y cruce con asistencia a concentraciones
 */

import { supabase as rawClient } from '../../lib/supabase'
import {
  OffensiveConcept,
  OffensivePlay,
  DefensiveCall,
  LineupTemplate,
  LineupTemplateSlot,
  PracticeScript,
  PlannedRep,
  ScriptConflict,
  RosterAthleteOption,
  ItemStatus,
} from '../../types/sports'

const supabase = rawClient!

export const sportsService = {
  // ==========================================================================
  // 1. CONCEPTOS OFENSIVOS
  // ==========================================================================

  async getOffensiveConcepts(
    teamId: string,
    status: 'active' | 'archived' | 'all' = 'active'
  ): Promise<OffensiveConcept[]> {
    let query = supabase
      .from('offensive_concepts')
      .select('*')
      .eq('team_id', teamId)
      .order('name', { ascending: true })

    if (status !== 'all') {
      query = query.eq('status', status)
    }

    const { data, error } = await query
    if (error) {
      console.error('Error al consultar conceptos ofensivos:', error)
      throw new Error(`Error al consultar conceptos: ${error.message}`)
    }
    return (data || []) as OffensiveConcept[]
  },

  async createOffensiveConcept(data: {
    teamId: string
    name: string
    description?: string
  }): Promise<OffensiveConcept> {
    const { data: teamData, error: teamErr } = await supabase
      .from('teams')
      .select('organization_id')
      .eq('id', data.teamId)
      .single()

    if (teamErr || !teamData) {
      throw new Error('Equipo no encontrado')
    }

    const { data: concept, error } = await supabase
      .from('offensive_concepts')
      .insert({
        organization_id: teamData.organization_id,
        team_id: data.teamId,
        name: data.name.trim(),
        description: data.description?.trim() || null,
        status: 'active',
      })
      .select()
      .single()

    if (error) {
      console.error('Error al crear concepto ofensivo:', error)
      throw new Error(`Error al crear concepto: ${error.message}`)
    }
    return concept as OffensiveConcept
  },

  async updateOffensiveConcept(
    id: string,
    data: { name?: string; description?: string; status?: ItemStatus }
  ): Promise<OffensiveConcept> {
    const updatePayload: Record<string, unknown> = {
      updated_at: new Date().toISOString(),
    }
    if (data.name !== undefined) updatePayload.name = data.name.trim()
    if (data.description !== undefined) updatePayload.description = data.description.trim() || null
    if (data.status !== undefined) updatePayload.status = data.status

    const { data: updated, error } = await supabase
      .from('offensive_concepts')
      .update(updatePayload)
      .eq('id', id)
      .select()
      .single()

    if (error) {
      console.error('Error al actualizar concepto ofensivo:', error)
      throw new Error(`Error al actualizar concepto: ${error.message}`)
    }
    return updated as OffensiveConcept
  },

  // ==========================================================================
  // 2. JUGADAS OFENSIVAS
  // ==========================================================================

  async getOffensivePlays(
    teamId: string,
    options?: {
      conceptId?: string
      status?: 'active' | 'archived' | 'all'
      search?: string
    }
  ): Promise<OffensivePlay[]> {
    let query = supabase
      .from('offensive_plays')
      .select(`
        *,
        concept:offensive_concepts!offensive_plays_concept_id_fkey (
          id, name, status
        )
      `)
      .eq('team_id', teamId)
      .order('short_code', { ascending: true })

    if (options?.conceptId) {
      query = query.eq('concept_id', options.conceptId)
    }

    const statusFilter = options?.status || 'active'
    if (statusFilter !== 'all') {
      query = query.eq('status', statusFilter)
    }

    if (options?.search && options.search.trim()) {
      const q = `%${options.search.trim()}%`
      query = query.or(`name.ilike.${q},short_code.ilike.${q}`)
    }

    const { data, error } = await query
    if (error) {
      console.error('Error al consultar jugadas ofensivas:', error)
      throw new Error(`Error al consultar jugadas: ${error.message}`)
    }
    return (data || []) as OffensivePlay[]
  },

  async createOffensivePlay(data: {
    teamId: string
    conceptId: string
    name: string
    shortCode: string
    description?: string
    coachingNotes?: string
  }): Promise<OffensivePlay> {
    const { data: teamData, error: teamErr } = await supabase
      .from('teams')
      .select('organization_id')
      .eq('id', data.teamId)
      .single()

    if (teamErr || !teamData) {
      throw new Error('Equipo no encontrado')
    }

    const { data: play, error } = await supabase
      .from('offensive_plays')
      .insert({
        organization_id: teamData.organization_id,
        team_id: data.teamId,
        concept_id: data.conceptId,
        name: data.name.trim(),
        short_code: data.shortCode.trim().toUpperCase(),
        description: data.description?.trim() || null,
        coaching_notes: data.coachingNotes?.trim() || null,
        status: 'active',
      })
      .select(`
        *,
        concept:offensive_concepts!offensive_plays_concept_id_fkey (
          id, name, status
        )
      `)
      .single()

    if (error) {
      console.error('Error al crear jugada ofensiva:', error)
      throw new Error(`Error al crear jugada: ${error.message}`)
    }
    return play as OffensivePlay
  },

  async updateOffensivePlay(
    id: string,
    data: {
      conceptId?: string
      name?: string
      shortCode?: string
      description?: string
      coachingNotes?: string
      status?: ItemStatus
    }
  ): Promise<OffensivePlay> {
    const updatePayload: Record<string, unknown> = {
      updated_at: new Date().toISOString(),
    }
    if (data.conceptId !== undefined) updatePayload.concept_id = data.conceptId
    if (data.name !== undefined) updatePayload.name = data.name.trim()
    if (data.shortCode !== undefined) updatePayload.short_code = data.shortCode.trim().toUpperCase()
    if (data.description !== undefined) updatePayload.description = data.description.trim() || null
    if (data.coachingNotes !== undefined) updatePayload.coaching_notes = data.coachingNotes.trim() || null
    if (data.status !== undefined) updatePayload.status = data.status

    const { data: updated, error } = await supabase
      .from('offensive_plays')
      .update(updatePayload)
      .eq('id', id)
      .select(`
        *,
        concept:offensive_concepts!offensive_plays_concept_id_fkey (
          id, name, status
        )
      `)
      .single()

    if (error) {
      console.error('Error al actualizar jugada ofensiva:', error)
      throw new Error(`Error al actualizar jugada: ${error.message}`)
    }
    return updated as OffensivePlay
  },

  // ==========================================================================
  // 3. LLAMADAS DEFENSIVAS
  // ==========================================================================

  async getDefensiveCalls(
    teamId: string,
    options?: {
      coverageFamily?: string
      status?: 'active' | 'archived' | 'all'
      search?: string
    }
  ): Promise<DefensiveCall[]> {
    let query = supabase
      .from('defensive_calls')
      .select('*')
      .eq('team_id', teamId)
      .order('short_code', { ascending: true })

    if (options?.coverageFamily) {
      query = query.eq('coverage_family', options.coverageFamily)
    }

    const statusFilter = options?.status || 'active'
    if (statusFilter !== 'all') {
      query = query.eq('status', statusFilter)
    }

    if (options?.search && options.search.trim()) {
      const q = `%${options.search.trim()}%`
      query = query.or(`name.ilike.${q},short_code.ilike.${q}`)
    }

    const { data, error } = await query
    if (error) {
      console.error('Error al consultar llamadas defensivas:', error)
      throw new Error(`Error al consultar llamadas defensivas: ${error.message}`)
    }
    return (data || []) as DefensiveCall[]
  },

  async createDefensiveCall(data: {
    teamId: string
    name: string
    shortCode: string
    coverageFamily?: string
    description?: string
    coachingNotes?: string
  }): Promise<DefensiveCall> {
    const { data: teamData, error: teamErr } = await supabase
      .from('teams')
      .select('organization_id')
      .eq('id', data.teamId)
      .single()

    if (teamErr || !teamData) {
      throw new Error('Equipo no encontrado')
    }

    const { data: call, error } = await supabase
      .from('defensive_calls')
      .insert({
        organization_id: teamData.organization_id,
        team_id: data.teamId,
        name: data.name.trim(),
        short_code: data.shortCode.trim().toUpperCase(),
        coverage_family: data.coverageFamily?.trim() || null,
        description: data.description?.trim() || null,
        coaching_notes: data.coachingNotes?.trim() || null,
        status: 'active',
      })
      .select()
      .single()

    if (error) {
      console.error('Error al crear llamada defensiva:', error)
      throw new Error(`Error al crear llamada defensiva: ${error.message}`)
    }
    return call as DefensiveCall
  },

  async updateDefensiveCall(
    id: string,
    data: {
      name?: string
      shortCode?: string
      coverageFamily?: string
      description?: string
      coachingNotes?: string
      status?: ItemStatus
    }
  ): Promise<DefensiveCall> {
    const updatePayload: Record<string, unknown> = {
      updated_at: new Date().toISOString(),
    }
    if (data.name !== undefined) updatePayload.name = data.name.trim()
    if (data.shortCode !== undefined) updatePayload.short_code = data.shortCode.trim().toUpperCase()
    if (data.coverageFamily !== undefined) updatePayload.coverage_family = data.coverageFamily.trim() || null
    if (data.description !== undefined) updatePayload.description = data.description.trim() || null
    if (data.coachingNotes !== undefined) updatePayload.coaching_notes = data.coachingNotes.trim() || null
    if (data.status !== undefined) updatePayload.status = data.status

    const { data: updated, error } = await supabase
      .from('defensive_calls')
      .update(updatePayload)
      .eq('id', id)
      .select()
      .single()

    if (error) {
      console.error('Error al actualizar llamada defensiva:', error)
      throw new Error(`Error al actualizar llamada defensiva: ${error.message}`)
    }
    return updated as DefensiveCall
  },

  // ==========================================================================
  // 4. PLANTILLAS DE QUINTETOS (LINEUP TEMPLATES)
  // ==========================================================================

  async getLineupTemplates(
    teamId: string,
    options?: {
      unit?: 'offense' | 'defense'
      status?: 'active' | 'archived' | 'all'
    }
  ): Promise<LineupTemplate[]> {
    let query = supabase
      .from('lineup_templates')
      .select(`
        *,
        slots:lineup_template_slots (
          id,
          template_id,
          team_id,
          athlete_id,
          position,
          order_index,
          athlete:athletes!lineup_template_slots_athlete_id_fkey (
            id, first_name, last_name, dorsal_number, primary_position
          )
        )
      `)
      .eq('team_id', teamId)
      .order('name', { ascending: true })

    if (options?.unit) {
      query = query.eq('unit', options.unit)
    }

    const statusFilter = options?.status || 'active'
    if (statusFilter !== 'all') {
      query = query.eq('status', statusFilter)
    }

    const { data, error } = await query
    if (error) {
      console.error('Error al consultar plantillas de quinteto:', error)
      throw new Error(`Error al consultar quintetos: ${error.message}`)
    }

    // Ordenar slots por order_index
    const templates = ((data as unknown as (LineupTemplate & { slots: LineupTemplateSlot[] })[]) || []).map((t) => ({
      ...t,
      slots: (t.slots || []).sort(
        (a: LineupTemplateSlot, b: LineupTemplateSlot) => a.order_index - b.order_index
      ),
    }))

    return templates as LineupTemplate[]
  },

  async saveLineupTemplate(data: {
    templateId?: string
    teamId: string
    unit: 'offense' | 'defense'
    name: string
    slots: { athlete_id: string; position: string }[]
  }): Promise<{ success: boolean; template_id: string; name: string }> {
    const { data: res, error } = await supabase.rpc('save_lineup_template', {
      p_template_id: data.templateId || null,
      p_team_id: data.teamId,
      p_unit: data.unit,
      p_name: data.name,
      p_slots: data.slots,
    })

    if (error) {
      console.error('Error en save_lineup_template:', error)
      throw new Error(`Error al guardar quinteto: ${error.message}`)
    }

    return res as { success: boolean; template_id: string; name: string }
  },

  async duplicateLineupTemplate(
    sourceTemplateId: string,
    newName: string
  ): Promise<{ success: boolean; new_template_id: string; name: string }> {
    const { data: res, error } = await supabase.rpc('duplicate_lineup_template', {
      p_source_template_id: sourceTemplateId,
      p_new_name: newName.trim(),
    })

    if (error) {
      console.error('Error en duplicate_lineup_template:', error)
      throw new Error(`Error al duplicar quinteto: ${error.message}`)
    }

    return res as { success: boolean; new_template_id: string; name: string }
  },

  async archiveLineupTemplate(id: string): Promise<void> {
    const { error } = await supabase
      .from('lineup_templates')
      .update({ status: 'archived', updated_at: new Date().toISOString() })
      .eq('id', id)

    if (error) {
      console.error('Error al archivar plantilla:', error)
      throw new Error(`Error al archivar plantilla: ${error.message}`)
    }
  },

  // ==========================================================================
  // 5. GUIONES DE ENTRENAMIENTO (PRACTICE SCRIPTS) Y REPETICIONES
  // ==========================================================================

  async getPracticeScripts(
    teamId: string,
    options?: {
      sessionId?: string
      blockId?: string
    }
  ): Promise<PracticeScript[]> {
    let query = supabase
      .from('practice_scripts')
      .select(`
        *,
        reps:planned_reps (
          id
        )
      `)
      .eq('team_id', teamId)
      .order('created_at', { ascending: false })

    if (options?.sessionId) {
      query = query.eq('session_id', options.sessionId)
    }
    if (options?.blockId) {
      query = query.eq('block_id', options.blockId)
    }

    const { data, error } = await query
    if (error) {
      console.error('Error al consultar guiones de práctica:', error)
      throw new Error(`Error al consultar guiones: ${error.message}`)
    }

    const scripts = ((data as unknown as (PracticeScript & { reps?: { id: string }[] })[]) || []).map((s) => ({
      ...s,
      reps_count: Array.isArray(s.reps) ? s.reps.length : 0,
    }))

    return scripts as PracticeScript[]
  },

  async getPracticeScriptById(id: string): Promise<PracticeScript> {
    const { data, error } = await supabase
      .from('practice_scripts')
      .select(`
        *,
        reps:planned_reps (
          *
        )
      `)
      .eq('id', id)
      .single()

    if (error || !data) {
      console.error('Error al consultar guion de práctica:', error)
      throw new Error(`Guion no encontrado: ${error?.message || ''}`)
    }

    const sortedReps = (data.reps || []).sort(
      (a: PlannedRep, b: PlannedRep) => a.order_index - b.order_index
    )

    return {
      ...data,
      reps: sortedReps,
      reps_count: sortedReps.length,
    } as PracticeScript
  },

  async savePracticeScript(
    script: Partial<PracticeScript> & { team_id: string; name: string },
    reps: Partial<PlannedRep>[]
  ): Promise<{ success: boolean; script_id: string; status: string; reps_count: number }> {
    const { data: res, error } = await supabase.rpc('save_practice_script', {
      p_script: script,
      p_reps: reps,
    })

    if (error) {
      console.error('Error en save_practice_script:', error)
      throw new Error(`Error al guardar guion: ${error.message}`)
    }

    return res as { success: boolean; script_id: string; status: string; reps_count: number }
  },

  async duplicatePracticeScript(
    sourceScriptId: string,
    newName: string,
    newSessionId?: string | null,
    newBlockId?: string | null
  ): Promise<{ success: boolean; new_script_id: string; name: string; reps_count: number }> {
    const { data: res, error } = await supabase.rpc('duplicate_practice_script', {
      p_source_script_id: sourceScriptId,
      p_new_name: newName.trim(),
      p_new_session_id: newSessionId || null,
      p_new_block_id: newBlockId || null,
    })

    if (error) {
      console.error('Error en duplicate_practice_script:', error)
      throw new Error(`Error al duplicar guion: ${error.message}`)
    }

    return res as {
      success: boolean
      new_script_id: string
      name: string
      reps_count: number
    }
  },

  async deletePracticeScript(id: string): Promise<void> {
    const { error } = await supabase.from('practice_scripts').delete().eq('id', id)
    if (error) {
      console.error('Error al borrar guion:', error)
      throw new Error(`Error al borrar guion: ${error.message}`)
    }
  },

  // ==========================================================================
  // 6. DETECCIÓN DE CONFLICTOS Y REGLAS DE DOMINIO
  // ==========================================================================

  detectScriptConflicts(
    reps: PlannedRep[],
    attendanceMap?: Map<string, { status: string; athleteName: string }>
  ): ScriptConflict[] {
    const conflicts: ScriptConflict[] = []

    reps.forEach((rep, idx) => {
      const repNum = rep.order_index || idx + 1
      const offSlots = rep.offense_slots || []
      const defSlots = rep.defense_slots || []

      const offMap = new Map<string, { position: string; name?: string }>()
      offSlots.forEach((s) => {
        if (s.athlete_id) {
          offMap.set(s.athlete_id, {
            position: s.position,
            name: s.athlete_name || (s as { full_name?: string }).full_name,
          })
        }
      })

      // 1. Conflicto dual: mismo atleta en ambos quintetos enfrentados
      defSlots.forEach((defSlot) => {
        if (defSlot.athlete_id && offMap.has(defSlot.athlete_id)) {
          const off = offMap.get(defSlot.athlete_id)!
          const name =
            defSlot.athlete_name ||
            (defSlot as { full_name?: string }).full_name ||
            off.name ||
            'Atleta'
          conflicts.push({
            type: 'dual_sided_player',
            repIndex: repNum,
            athleteId: defSlot.athlete_id,
            athleteName: name,
            offensePosition: off.position,
            defensePosition: defSlot.position,
            description: `Rep #${repNum}: ${name} figura simultáneamente en Ataque (${off.position}) y Defensa (${defSlot.position}).`,
          })
        }
      })

      // 2. Alertas de asistencia si se proporciona attendanceMap (retirados o declinados)
      if (attendanceMap) {
        const allSlotAthletes = [
          ...offSlots.map((s) => ({
            id: s.athlete_id,
            pos: `Ataque - ${s.position}`,
            name: s.athlete_name || (s as { full_name?: string }).full_name,
          })),
          ...defSlots.map((s) => ({
            id: s.athlete_id,
            pos: `Defensa - ${s.position}`,
            name: s.athlete_name || (s as { full_name?: string }).full_name,
          })),
        ]

        allSlotAthletes.forEach((item) => {
          if (!item.id) return
          const att = attendanceMap.get(item.id)
          if (att?.status === 'withdrawn') {
            conflicts.push({
              type: 'attendance_withdrawn',
              repIndex: repNum,
              athleteId: item.id,
              athleteName: item.name || att.athleteName || 'Atleta',
              description: `Rep #${repNum}: ${item.name || att.athleteName} (${item.pos}) figura como retirado de la concentración.`,
            })
          } else if (att?.status === 'declined') {
            conflicts.push({
              type: 'attendance_declined',
              repIndex: repNum,
              athleteId: item.id,
              athleteName: item.name || att.athleteName || 'Atleta',
              description: `Rep #${repNum}: ${item.name || att.athleteName} (${item.pos}) ha declinado asistencia a la concentración.`,
            })
          }
        })
      }
    })

    return conflicts
  },

  // ==========================================================================
  // 7. ROSTER Y CRUCE CON CONVOCATORIA (EVENTO / SESIÓN)
  // ==========================================================================

  async getTeamAthletesWithEventContext(
    teamId: string,
    eventId?: string
  ): Promise<RosterAthleteOption[]> {
    // 1. Obtener atletas activos en el roster del equipo
    const { data: rosterData, error: rosterErr } = await supabase
      .from('roster_entries')
      .select(`
        unit,
        athlete:athletes!roster_entries_athlete_id_fkey (
          id, first_name, last_name, dorsal_number, primary_position, user_id
        )
      `)
      .eq('team_id', teamId)
      .eq('status', 'active')

    if (rosterErr) {
      console.error('Error al consultar roster:', rosterErr)
      throw new Error(`Error al consultar atletas: ${rosterErr.message}`)
    }

    // 2. Si hay eventId, obtener participantes convocados
    const attendanceMap = new Map<string, 'confirmed' | 'pending' | 'declined' | 'withdrawn'>()
    if (eventId) {
      const { data: partData } = await supabase
        .from('event_participants')
        .select('athlete_id, attendance_status')
        .eq('event_id', eventId)

      if (partData) {
        partData.forEach((p: { athlete_id: string | null; attendance_status: string }) => {
          if (p.athlete_id) {
            attendanceMap.set(
              p.athlete_id,
              p.attendance_status as 'confirmed' | 'pending' | 'declined' | 'withdrawn'
            )
          }
        })
      }
    }

    const athletes: RosterAthleteOption[] = []
    ;(rosterData || []).forEach((row: { unit?: string; athlete: unknown }) => {
      const a = row.athlete as unknown as {
        id: string
        first_name: string
        last_name: string
        dorsal_number?: number | null
        primary_position: string
        user_id?: string | null
      }
      if (!a) return

      let attendanceStatus: 'confirmed' | 'pending' | 'declined' | 'withdrawn' | 'not_summoned' =
        'not_summoned'
      if (eventId) {
        if (attendanceMap.has(a.id)) {
          attendanceStatus = attendanceMap.get(a.id)!
        }
      }

      athletes.push({
        id: a.id,
        first_name: a.first_name,
        last_name: a.last_name,
        full_name: `${a.first_name} ${a.last_name}`.trim(),
        dorsal_number: a.dorsal_number,
        primary_position: a.primary_position,
        unit: (row.unit as 'offense' | 'defense') || 'offense',
        user_id: a.user_id,
        attendance_status: eventId ? attendanceStatus : undefined,
      })
    })

    return athletes.sort((a, b) => a.full_name.localeCompare(b.full_name))
  },

  async getRosterAthleteOptions(teamId: string, eventId?: string): Promise<RosterAthleteOption[]> {
    return this.getTeamAthletesWithEventContext(teamId, eventId)
  },
}

